import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Input, Space, Tag, Popconfirm, Tooltip, Flex,
  Typography, Card, message,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined,
  SyncOutlined, LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as adminactions from 'store/actions/AuthActions';
import EventFormModal from './EventFormModal';
import useUrlFilters from 'hooks/useUrlFilters';

const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const { Title } = Typography;

const eventTypeColors = {
  meeting: 'blue',
  event: 'green',
  reminder: 'orange',
};

const visibilityLabels = {
  all: 'All Employees',
  department: 'Departments',
  employee: 'Specific Employees',
};

const Events = () => {
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  // Events table state (search and page live in the URL)
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);
  const searchTerm = filters.search || '';
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // Dropdown data
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Outlook Calendar Sync state
  const [outlookSyncs, setOutlookSyncs] = useState([]);
  const [syncsLoading, setSyncsLoading] = useState(false);
  const [newIcsUrl, setNewIcsUrl] = useState('');
  const [addingSync, setAddingSync] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const fetchEvents = useCallback(async (page, size) => {
    setLoading(true);
    try {
      const response = await adminactions.loadEvents(page, searchTerm, size);
      if (response.status === 200) {
        setDataSource(response.data.results || []);
        setTotalRows(response.data.count || 0);
      }
    } catch (e) {
      console.error('Error fetching events:', e);
    }
    setLoading(false);
  }, [searchTerm]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        adminactions.loadAllEmployees(),
        adminactions.loadDepartments(),
      ]);
      if (empRes?.data?.results) setEmployees(empRes.data.results);
      if (Array.isArray(deptRes)) setDepartments(deptRes);
    } catch (e) {
      console.error('Error fetching dropdown data:', e);
    }
  }, []);

  const fetchOutlookSyncs = useCallback(async () => {
    setSyncsLoading(true);
    try {
      const data = await adminactions.getOutlookSyncs();
      setOutlookSyncs(data?.results || (Array.isArray(data) ? data : []));
    } catch (e) {
      console.error('Error fetching outlook syncs:', e);
    }
    setSyncsLoading(false);
  }, []);

  const handleAddSync = async () => {
    if (!newIcsUrl.trim()) {
      message.warning('Please enter an ICS URL');
      return;
    }
    setAddingSync(true);
    try {
      await adminactions.createOutlookSync({ ics_url: newIcsUrl.trim(), is_active: true });
      message.success('Calendar sync added');
      setNewIcsUrl('');
      fetchOutlookSyncs();
    } catch (e) {
      message.error(e?.response?.data?.ics_url?.[0] || 'Failed to add calendar sync');
    }
    setAddingSync(false);
  };

  const handleDeleteSync = async (id) => {
    try {
      await adminactions.deleteOutlookSync(id);
      message.success('Calendar sync removed');
      fetchOutlookSyncs();
    } catch (e) {
      message.error('Failed to delete calendar sync');
    }
  };

  const handleTriggerSync = async (id) => {
    setSyncingId(id);
    try {
      await adminactions.triggerOutlookSync(id);
      message.success('Sync completed');
      fetchOutlookSyncs();
    } catch (e) {
      message.error('Sync failed');
    }
    setSyncingId(null);
  };

  useEffect(() => {
    fetchEvents(currentPage, pageSize);
  }, [currentPage, pageSize, fetchEvents]);

  useEffect(() => {
    fetchDropdownData();
    fetchOutlookSyncs();
  }, [fetchDropdownData, fetchOutlookSyncs]);

  const handleTableChange = (page, size) => {
    // A new page size starts again from page 1.
    setCurrentPage(size !== pageSize ? 1 : page);
    setPageSize(size);
  };

  const onSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleDelete = useCallback(async (id) => {
    const response = await adminactions.deleteEvent(id);
    if (response.status === 204) {
      message.success('Event deleted');
      fetchEvents(currentPage, pageSize);
    }
  }, [currentPage, pageSize, fetchEvents]);

  const handleEdit = useCallback(async (id) => {
    try {
      const data = await adminactions.fetchEvent(id);
      if (data) {
        setEditingRecord(data);
        setModalVisible(true);
      }
    } catch (e) {
      message.error('Failed to load event details');
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a?.title || '').localeCompare(b?.title || ''),
      width: 250,
    },
    {
      title: 'DATE',
      dataIndex: 'event_date',
      key: 'event_date',
      width: 120,
      sorter: (a, b) => (a?.event_date || '').localeCompare(b?.event_date || ''),
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
    },
    {
      title: 'TIME',
      key: 'time',
      width: 150,
      render: (_, record) => {
        if (record.is_all_day) return <Tag>All Day</Tag>;
        const start = record.start_time ? dayjs(record.start_time, 'HH:mm:ss').format('hh:mm A') : '';
        const end = record.end_time ? dayjs(record.end_time, 'HH:mm:ss').format('hh:mm A') : '';
        return start ? `${start}${end ? ' - ' + end : ''}` : '-';
      },
    },
    {
      title: 'TYPE',
      dataIndex: 'event_type',
      key: 'event_type',
      width: 100,
      render: (type) => (
        <Tag color={eventTypeColors[type] || 'default'}>
          {type ? type.charAt(0).toUpperCase() + type.slice(1) : '-'}
        </Tag>
      ),
    },
    {
      title: 'VISIBILITY',
      dataIndex: 'visibility_type',
      key: 'visibility_type',
      width: 140,
      render: (type) => visibilityLabels[type] || type,
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (val) => val || '-',
    },
    {
      title: 'CREATED BY',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 140,
      render: (val) => val || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Flex gap="middle">
          <Tooltip title="Edit">
            <a onClick={() => handleEdit(record.id)}><EditOutlined /></a>
          </Tooltip>
          <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
            <a><DeleteOutlined /></a>
          </Popconfirm>
        </Flex>
      ),
    },
  ], [handleDelete, handleEdit]);

  const syncColumns = useMemo(() => [
    {
      title: 'ICS URL',
      dataIndex: 'ics_url',
      key: 'ics_url',
      ellipsis: true,
      render: (url) => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <LinkOutlined style={{ marginRight: 4 }} />
            {url}
          </a>
        </Tooltip>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'LAST SYNCED',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      width: 180,
      render: (dt) => dt ? dayjs(dt).format('DD MMM YYYY, hh:mm A') : 'Never',
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Sync Now">
            <Button
              size="small"
              icon={<SyncOutlined spin={syncingId === record.id} />}
              loading={syncingId === record.id}
              onClick={() => handleTriggerSync(record.id)}
            >
              Sync
            </Button>
          </Tooltip>
          <Popconfirm title="Remove this calendar sync?" onConfirm={() => handleDeleteSync(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ], [syncingId]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Events Section */}
      <Card>
        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Events & Meetings
          </Title>
          <Space>
            <Input.Search
              placeholder="Search events..."
              value={searchTermLive}
              onChange={(e) => setSearchTermLive(e.target.value)}
              onSearch={onSearch}
              style={{ width: 250 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRecord(null);
                setModalVisible(true);
              }}
            >
              Create Event
            </Button>
          </Space>
        </Flex>

        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: handleTableChange,
          }}
        />
      </Card>

      {/* Outlook Calendar Sync Section */}
      <Card style={{ marginTop: 16 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 16 }}>
          <SyncOutlined style={{ marginRight: 8 }} />
          Outlook Calendar Sync
        </Title>

        <Flex gap="small" style={{ marginBottom: 16 }}>
          <Input
            placeholder="Paste .ics calendar URL..."
            value={newIcsUrl}
            onChange={(e) => setNewIcsUrl(e.target.value)}
            onPressEnter={handleAddSync}
            style={{ maxWidth: 500 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={addingSync}
            onClick={handleAddSync}
          >
            Add
          </Button>
        </Flex>

        <Table
          columns={syncColumns}
          dataSource={outlookSyncs}
          rowKey="id"
          loading={syncsLoading}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No calendar syncs configured' }}
        />
      </Card>

      {/* Event Form Modal */}
      <EventFormModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
        }}
        onSuccess={() => {
          setModalVisible(false);
          setEditingRecord(null);
          fetchEvents(currentPage, pageSize);
        }}
        editingRecord={editingRecord}
        employees={employees}
        departments={departments}
      />
    </div>
  );
};

export default Events;
